import {
  type LngLat,
  type MapLocationRequest,
  Box,
  Button,
  Divider,
  IconButton,
  Image,
  LeftArrowIcon,
  MapGeolocationCustomControl,
  Paper,
  Skeleton,
  Text,
  Typography,
} from '@yandex-delivery/blocks'
import {useI18n} from '@logistics-frontend/core/i18n'
import {useDebounce} from '@yandex-delivery/hooks'
import {tag2url} from '@yandex-delivery/utils'
import {useConfigContext} from '@yandex-delivery/form'
import {MAP_ANIMATION_DURATION_IN_MS} from '@yandex-delivery/form/utils/constants'
import {memo, useCallback, useEffect, useRef, useState} from 'react'

import {type AddressPin, useFetchAddressPin} from '../../../../../../../../api/address'
import * as styles from './AddressInfo.classes'

/** Пропсы компонента AddressInfo */
type AddressInfoProps = {
  /** Координаты текущей точки на карте */
  coordinates: LngLat
  /** Является ли текущий выбираемый адрес - точкой "Откуда" */
  isSource: boolean
  /** Коллбэк на закрытие модалки с картой */
  onClose: VoidFunction
  /** Коллбэк на сохранение выбранного адреса */
  onSave: (addressPin: AddressPin) => void
  onLocationChange: (coordinates: LngLat) => void
  setLocation: (location: MapLocationRequest) => void
  setAddressInfoHeight?: (height: number) => void
}

/** Ожидание до перезапроса адреса после очередного изменения координат */
const MUTATE_ADDRESS_PIN_DEBOUNCE_WAIT_IN_MS = 500

/** Тег иконки бублика, которая выводится рядом с адресом */
const DOT_OUTLINE_ICON = 'delivery_dot_outline'

/**
 * Компонент с текущим отмеченным адресом и кнопкой сохранения.
 */
export const AddressInfo = memo<AddressInfoProps>(function AddressInfo({
  coordinates,
  isSource,
  onClose,
  onSave,
  setLocation,
  onLocationChange,
  setAddressInfoHeight,
}) {
  const i18n = useI18n()
  const {location: currentUserLocation} = useConfigContext()

  const [addressPin, setAddressPin] = useState<AddressPin | null>(null)

  const {
    mutate: mutateAddressPin,
    isPending: isAddressPinPending,
    error: addressPinError,
  } = useFetchAddressPin({onSuccess: setAddressPin})

  const handleRetry = useCallback(() => {
    mutateAddressPin({coordinates})
  }, [coordinates, mutateAddressPin])

  const handleSave = useCallback(() => {
    if (!addressPin) {
      return
    }

    onSave(addressPin)
    onClose()
  }, [addressPin, onClose, onSave])

  useDebounce(
    () => {
      mutateAddressPin({coordinates})
    },
    MUTATE_ADDRESS_PIN_DEBOUNCE_WAIT_IN_MS,
    [coordinates],
  )

  // Дисейблим кнопка сохранения, если в данный момент идёт запрос за адресом, либо адреса пока нет
  const isSaveButtonDisabled = isAddressPinPending || !addressPin

  // Кнопку для ретрая или текст ошибки показываем если есть ошибка, при этом в текущий момент нет запроса за адресом
  const isError = Boolean(addressPinError) && !isAddressPinPending
  const errorMessage = addressPinError?.message

  // Адрес можно показывать, когда он существует локально, и мы не ждём его перезапроса
  const isAddressReady = !isAddressPinPending && addressPin !== null

  const handleLocatePosition = useCallback(
    (coordinates: LngLat) => {
      setLocation({center: coordinates, duration: MAP_ANIMATION_DURATION_IN_MS, zoom: 17}) // !! без зума крашится приложение
      onLocationChange(coordinates)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onLocationChange],
  )

  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    if (ref.current) setAddressInfoHeight?.(ref.current.clientHeight)
  }, [setAddressInfoHeight, isAddressReady])

  return (
    <Paper ref={ref} className={styles.root} flexDirection="column" float="bottom">
      <IconButton className={styles.backButton} onPress={onClose} shape="circle" variant="floating">
        <LeftArrowIcon />
      </IconButton>
      <MapGeolocationCustomControl
        classes={styles.geolocationControl}
        initialLocation={currentUserLocation}
        onGeolocatePosition={handleLocatePosition}
      />
      <Paper className={styles.header} item="header" px="m" py="l">
        <Typography thickness="medium" variant="body1">
          {isSource
            ? i18n('bdui.address.map.from_title', 'Откуда забрать посылку')
            : i18n('bdui.address.map.to_title', 'Куда доставить посылку')}
        </Typography>
      </Paper>
      <Divider />
      {!isError && (
        <Box alignItems="center" className={styles.addressName} gap="m" px="l" py="m">
          <Image height={14} src={tag2url(DOT_OUTLINE_ICON)} />
          {!isAddressReady ? (
            <Skeleton size="xl" />
          ) : (
            <Text>
              <Typography variant="body2">{addressPin.title}</Typography>
              {addressPin.subtitle && (
                <Typography color="minor" variant="body2">
                  {addressPin.subtitle}
                </Typography>
              )}
            </Text>
          )}
        </Box>
      )}
      <Paper item="footer" pb="s" px="s">
        {isError ? (
          errorMessage ? (
            <Typography color="error" justifyContent="center" variant="body2">
              {errorMessage}
            </Typography>
          ) : (
            <Button onPress={handleRetry} variant="error" fullWidth>
              {i18n('bdui.general-error-repeat', 'Что-то пошло не так, повторить?')}
            </Button>
          )
        ) : (
          <Button disabled={isSaveButtonDisabled} onPress={handleSave} size="l" variant="action">
            {i18n('bdui.address.map.save', 'Сохранить')}
          </Button>
        )}
      </Paper>
    </Paper>
  )
})
